/**
 * useLeads - Hook simplificado para ver solicitudes de contacto
 * Solo lectura, sin gestión de estados
 */

import { useState, useEffect } from 'react'
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore'
import { db } from '../firebaseConfig'

// Tipos
export interface Lead {
  id: string
  nombre: string
  email: string
  telefono: string
  mensaje: string
  tipoContacto: 'informacion' | 'visita' | 'oferta' | 'otro'
  propertyId: string
  propertyName: string
  asesorIds: string[]
  inmobiliariaId: string | null
  fechaCreacion: Timestamp
  origen: string
}

interface UseLeadsOptions {
  asesorId?: string | string[]
  limitResults?: number
}

interface UseLeadsReturn {
  leads: Lead[]
  loading: boolean
  error: string | null
  totalLeads: number
  refetch: () => Promise<void>
}

export function useLeads(options: UseLeadsOptions = {}): UseLeadsReturn {
  const { asesorId, limitResults = 50 } = options

  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Normalize asesorId to array of non-empty strings
  const ids: string[] = Array.isArray(asesorId)
    ? asesorId.filter(Boolean)
    : (asesorId ? [asesorId] : [])

  // Fetch leads
  const fetchLeads = async () => {
    if (ids.length === 0) {
      setLeads([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Usa array-contains-any cuando hay múltiples perfil_ids (hasta 30).
      // Un asesor puede tener perfil duplicado en asesor/asociado; aquí
      // buscamos leads que apunten a cualquiera de sus docIds.
      const q = ids.length === 1
        ? query(collection(db, 'leads'), where('asesorIds', 'array-contains', ids[0]))
        : query(collection(db, 'leads'), where('asesorIds', 'array-contains-any', ids.slice(0, 30)))

      const snapshot = await getDocs(q)

      let leadsData: Lead[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Lead))

      // Ordenar en cliente por fecha (más recientes primero)
      leadsData.sort((a, b) => {
        const fechaA = a.fechaCreacion?.toDate?.() || new Date(0)
        const fechaB = b.fechaCreacion?.toDate?.() || new Date(0)
        return fechaB.getTime() - fechaA.getTime()
      })

      // Limitar resultados
      if (limitResults > 0) {
        leadsData = leadsData.slice(0, limitResults)
      }

      setLeads(leadsData)
    } catch (err: any) {
      console.error('Error fetching leads:', err)
      setError('No se pudieron cargar las solicitudes')
    } finally {
      setLoading(false)
    }
  }

  // Efecto para cargar leads inicial. Usa join estable del array como dep
  // para evitar re-fetch en cada render cuando el array se re-crea.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (ids.length > 0) {
      fetchLeads()
    } else {
      setLeads([])
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join('|')])

  return {
    leads,
    loading,
    error,
    totalLeads: leads.length,
    refetch: fetchLeads
  }
}

// Funciones utilitarias
export const formatearFechaLead = (timestamp: Timestamp): string => {
  if (!timestamp?.toDate) return 'Sin fecha'

  const fecha = timestamp.toDate()
  const ahora = new Date()
  const diffMs = ahora.getTime() - fecha.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHoras = Math.floor(diffMs / 3600000)
  const diffDias = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Hace un momento'
  if (diffMins < 60) return `Hace ${diffMins} min`
  if (diffHoras < 24) return `Hace ${diffHoras}h`
  if (diffDias < 7) return `Hace ${diffDias} día${diffDias > 1 ? 's' : ''}`

  return fecha.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: fecha.getFullYear() !== ahora.getFullYear() ? 'numeric' : undefined
  })
}

export const getTipoContactoLabel = (tipo: Lead['tipoContacto']): string => {
  const labels: Record<Lead['tipoContacto'], string> = {
    informacion: 'Información',
    visita: 'Agendar visita',
    oferta: 'Oferta',
    otro: 'Otro'
  }
  return labels[tipo] || tipo
}
